import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";
import { useMapFilters } from "../../hooks/mapDataAndFilters";
import { EmissionProviderPicker } from "./EmissionsControl";

const RAMPS = [
    {
        id: "heat",
        style: "linear-gradient(to right, #042C53, #1D9E75, #EF9F27, #E24B4A)",
    },
    {
        id: "cool",
        style: "linear-gradient(to right, #E6F1FB, #378ADD, #042C53)",
    },
    {
        id: "viridis",
        style: "linear-gradient(to right, #4B1528, #993556, #EF9F27, #EAF3DE)",
    },
];

const OpacitySlider = ({
    value,
    onChange,
}: {
    value: number;
    onChange: (v: number) => void;
}) => {
    const [local, setLocal] = useState(value);
    const debounced = useDebounce(local, 150);

    useEffect(() => {
        setLocal(value);
    }, [value]);

    useEffect(() => {
        if (debounced !== value) onChange(debounced);
    }, [debounced]);

    return (
        <label className="flex flex-col gap-1 px-1">
            <div className="flex justify-between">
                <span className="text-xs text-gray-500">Opacity</span>
                <span className="text-xs font-medium text-gray-800">
                    {local}%
                </span>
            </div>
            <input
                type="range"
                min={10}
                max={100}
                step={1}
                value={local}
                onChange={(e) => setLocal(Number(e.target.value))}
                className="w-full"
            />
        </label>
    );
};

export const HeatmapControls = () => {
    const { filterState, updateMapFilterState } = useMapFilters("mainMap");

    return (
        <div>
            <EmissionProviderPicker
                label="Data providers"
                providerFilters={{ hasData: true }}
                selected={
                    filterState.analytics.emissionHeatmaps.filters
                        .providerWithSource
                }
                setSelected={(v) =>
                    updateMapFilterState((d) => {
                        d.analytics.emissionHeatmaps.filters.providerWithSource =
                            v;
                        return d;
                    })
                }
            />

            <label className="flex flex-col gap-1 px-1 mb-2 mt-1">
                <span className="text-xs text-gray-500">
                    Intensity weighted by
                </span>
                <select
                    value={filterState.analytics.emissionHeatmaps.sourceData}
                    onChange={(e) =>
                        updateMapFilterState((d) => {
                            d.analytics.emissionHeatmaps.sourceData = e.target
                                .value as any;
                            return d;
                        })
                    }
                    className="text-xs w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-gray-800"
                >
                    <option value="percentage_of_obs">
                        Percentage of total observations
                    </option>
                    <option value="count_emissions">Detection count</option>
                    <option value="count_non_detects">Non-detect count</option>
                </select>
            </label>

            <div className="flex flex-col gap-1.5 px-1 mb-3">
                <span className="text-xs text-gray-500">Color ramp</span>
                <div className="flex gap-1.5">
                    {RAMPS.map((r) => (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() =>
                                updateMapFilterState((d) => {
                                    d.analytics.emissionHeatmaps.colorMap =
                                        r.id as any;
                                    return d;
                                })
                            }
                            className="h-4 flex-1 rounded"
                            style={{
                                background: r.style,
                                outline:
                                    filterState.analytics.emissionHeatmaps
                                        .colorMap === r.id
                                        ? "2px solid #0F6E56"
                                        : "2px solid transparent",
                                outlineOffset: "1px",
                            }}
                            aria-label={`${r.id} color ramp`}
                        />
                    ))}
                </div>
            </div>

            <OpacitySlider
                value={filterState.analytics.emissionHeatmaps.opacity}
                onChange={(v) =>
                    updateMapFilterState((d) => {
                        d.analytics.emissionHeatmaps.opacity = v;
                        return d;
                    })
                }
            />
        </div>
    );
};
